import {
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  useRef,
} from "react";
import { createPortal } from "react-dom";
import { motion, useReducedMotion } from "framer-motion";
import { Volume2, VolumeX, X } from "lucide-react";

import Dialog from "@/blueprint/components/Dialog/Dialog";
import IconButton from "@/blueprint/components/IconButton/IconButton";
import PauseIcon from "@/blueprint/components/IconButton/PauseIcon";
import PlayIcon from "@/blueprint/components/IconButton/PlayIcon";
import { MotionDuration } from "@/blueprint/tokens/Motion";
import { cn } from "@/lib/utils";

import { useVideoPlayer } from "./useVideoPlayer";

type VideoPlayerModalProps = {
  videoSrc: string | null;
  posterSrc: string;
  posterAlt: string;
  /**
   * The frame the media plays in — its shape, and which side it is sized from.
   * Size from the width (`w-full`) whenever the shape is responsive: `h-full`
   * is an explicit height that a clamped width cannot shrink, so the frame
   * would stop matching the shape it declares. Defaults to the phone-shaped
   * portrait the testimonial players use.
   */
  frameClassName?: string;
  /**
   * How the media fills that frame. Footage shown whole wants `object-contain`,
   * so a short window letterboxes it rather than cropping; a frame that
   * deliberately crops to an orientation keeps the default.
   */
  mediaFitClassName?: string;
  /** The dialog's name, and its close button's; the testimonial players'
      by default. */
  label?: string;
  closeLabel?: string;
  onClose: () => void;
};

const SEEK_STEP_SECONDS = 5;

const formatTime = (seconds: number) => {
  const wholeSeconds = Math.max(0, Math.floor(seconds));
  const minutes = Math.floor(wholeSeconds / 60);
  const remainingSeconds = wholeSeconds % 60;

  return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
};

const VideoPlayerModal = ({
  videoSrc,
  posterSrc,
  posterAlt,
  frameClassName = "h-full max-w-full [aspect-ratio:549/976]",
  mediaFitClassName = "object-cover",
  label = "Testimonial video",
  closeLabel = "Close testimonial video",
  onClose,
}: VideoPlayerModalProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const timelineRef = useRef<HTMLDivElement>(null);
  const reducedMotion = useReducedMotion() === true;
  const {
    isPlaying,
    isMuted,
    currentTime,
    duration,
    togglePlayback,
    toggleSound,
    seekToRatio,
    videoEventHandlers,
  } = useVideoPlayer(videoRef, videoSrc);

  const seekToPointer = (event: ReactPointerEvent<HTMLDivElement>) => {
    const timeline = timelineRef.current;
    if (timeline === null || duration === 0) return;

    const bounds = timeline.getBoundingClientRect();
    const ratio = Math.min(
      1,
      Math.max(0, (event.clientX - bounds.left) / bounds.width),
    );
    seekToRatio(ratio);
  };

  const handleTimelinePointerDown = (
    event: ReactPointerEvent<HTMLDivElement>,
  ) => {
    event.currentTarget.setPointerCapture(event.pointerId);
    seekToPointer(event);
  };

  const handleTimelineKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (duration === 0) return;

    let nextTime: number;

    switch (event.key) {
      case "ArrowLeft":
      case "ArrowDown":
        nextTime = currentTime - SEEK_STEP_SECONDS;
        break;
      case "ArrowRight":
      case "ArrowUp":
        nextTime = currentTime + SEEK_STEP_SECONDS;
        break;
      case "Home":
        nextTime = 0;
        break;
      case "End":
        nextTime = duration;
        break;
      default:
        return;
    }

    event.preventDefault();
    seekToRatio(Math.min(duration, Math.max(0, nextTime)) / duration);
  };

  if (typeof document === "undefined") return null;

  const progress = duration === 0 ? 0 : (currentTime / duration) * 100;

  return createPortal(
    <Dialog
      open
      onClose={onClose}
      aria-label={label}
      className="fixed inset-0 z-[60]"
    >
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{
          duration: reducedMotion ? 0 : MotionDuration.quickMs / 1000,
        }}
        onPointerDown={(event) => {
          if (event.target === event.currentTarget) onClose();
        }}
        className="flex size-full items-center justify-center bg-tint-black-70 p-full backdrop-blur-[8px]"
      >
        <div
          className={cn("relative overflow-hidden rounded-s", frameClassName)}
        >
          <video
            ref={videoRef}
            poster={posterSrc}
            aria-label={posterAlt}
            playsInline
            preload="metadata"
            className={cn("size-full", mediaFitClassName)}
            {...videoEventHandlers}
          >
            {videoSrc !== null && <source src={videoSrc} type="video/mp4" />}
          </video>

          <div className="absolute inset-x-0 bottom-0 flex items-center gap-m bg-gradient-to-b from-transparent to-black/40 p-card-sm text-brand-white">
            <button
              type="button"
              aria-label={isPlaying ? "Pause video" : "Play video"}
              onClick={togglePlayback}
              className="flex size-xl shrink-0 items-center justify-center"
            >
              {isPlaying ? <PauseIcon /> : <PlayIcon />}
            </button>

            <div
              ref={timelineRef}
              role="slider"
              tabIndex={0}
              aria-label="Video timeline"
              aria-valuemin={0}
              aria-valuemax={duration}
              aria-valuenow={currentTime}
              aria-valuetext={`${formatTime(currentTime)} of ${formatTime(duration)}`}
              onPointerDown={handleTimelinePointerDown}
              onPointerMove={(event) => {
                if (event.currentTarget.hasPointerCapture(event.pointerId)) {
                  seekToPointer(event);
                }
              }}
              onKeyDown={handleTimelineKeyDown}
              className="flex h-xl flex-1 cursor-pointer touch-none items-center"
            >
              <div className="h-[2px] w-full bg-tint-white-20">
                <div
                  className="h-full bg-brand-white"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>

            <button
              type="button"
              aria-label={isMuted ? "Unmute video" : "Mute video"}
              onClick={toggleSound}
              className="flex size-xl shrink-0 items-center justify-center"
            >
              {isMuted ? (
                <VolumeX aria-hidden className="size-l" />
              ) : (
                <Volume2 aria-hidden className="size-l" />
              )}
            </button>
          </div>
        </div>

        <div className="absolute right-0 top-0 p-card-sm">
          <IconButton
            label={closeLabel}
            variant="secondary"
            onClick={onClose}
            className="[&>svg]:size-l"
          >
            <X aria-hidden strokeWidth={1.5} />
          </IconButton>
        </div>
      </motion.div>
    </Dialog>,
    document.body,
  );
};

export default VideoPlayerModal;
